import { useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "./adminApi";
import { formatPhoneFr, upperLastName } from "@/lib/formatters";
import PhoneInputFr from "@/components/PhoneInputFr";

const INITIAL = {
  civility: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "candidate",
  agency_id: "",
};

export default function CreateCandidateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(INITIAL);
  const [agencies, setAgencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(INITIAL);
    setErrors({});
    (async () => {
      try { const a = await api.get("/agencies"); setAgencies(a.data || []); } catch {}
    })();
  }, [open]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Requis";
    if (!form.last_name.trim()) e.last_name = "Requis";
    if (!form.email.trim()) e.email = "Requis";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Email invalide";
    if (!form.civility) e.civility = "Requise";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (andOpenProfile) => {
    if (!validate()) {
      toast.error("Veuillez corriger les champs en rouge");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/candidates", {
        ...form,
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
      });
      toast.success(`${r.data.name} créé avec succès`);
      onOpenChange(false);
      onCreated?.(r.data, andOpenProfile);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur lors de la création";
      toast.error(detail);
      if (/email/i.test(detail)) setErrors((e) => ({ ...e, email: detail }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none p-0" data-testid="create-candidate-dialog">
        <DialogHeader className="bg-gradient-to-r from-[#662483] to-[#0069B4] p-5">
          <DialogTitle className="text-white font-bold flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> Créer un candidat / salarié
          </DialogTitle>
          <p className="text-white/80 text-xs mt-1">
            Saisie rapide — les autres informations seront éditables depuis la fiche après création.
          </p>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {/* Civility + role */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Civilité *</Label>
              <Select value={form.civility} onValueChange={(v) => setField("civility", v)}>
                <SelectTrigger
                  className={`rounded-none ${errors.civility ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`}
                  data-testid="create-civility"
                >
                  <SelectValue placeholder="Sélectionnez…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monsieur">Monsieur</SelectItem>
                  <SelectItem value="madame">Madame</SelectItem>
                  <SelectItem value="mademoiselle">Mademoiselle</SelectItem>
                </SelectContent>
              </Select>
              {errors.civility && <p className="text-[10px] text-red-500 mt-0.5">{errors.civility}</p>}
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Rôle *</Label>
              <Select value={form.role} onValueChange={(v) => setField("role", v)}>
                <SelectTrigger className="rounded-none border-gray-300" data-testid="create-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="candidate">Candidat</SelectItem>
                  <SelectItem value="employee">Salarié</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Prénom *</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setField("first_name", e.target.value)}
                className={`rounded-none ${errors.first_name ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`}
                placeholder="Jean"
                data-testid="create-first-name"
              />
              {errors.first_name && <p className="text-[10px] text-red-500 mt-0.5">{errors.first_name}</p>}
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Nom *</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setField("last_name", upperLastName(e.target.value))}
                className={`rounded-none ${errors.last_name ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`}
                placeholder="DUPONT"
                data-testid="create-last-name"
              />
              {errors.last_name && <p className="text-[10px] text-red-500 mt-0.5">{errors.last_name}</p>}
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold mb-1 block">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                className={`rounded-none ${errors.email ? "border-red-500 ring-1 ring-red-300" : "border-gray-300"}`}
                placeholder="jean.dupont@email.fr"
                data-testid="create-email"
              />
              {errors.email && <p className="text-[10px] text-red-500 mt-0.5">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Téléphone</Label>
              <PhoneInputFr
                value={form.phone}
                onChange={(v) => setField("phone", v)}
                data-testid="create-phone"
              />
            </div>
          </div>

          {/* Agency (optional) */}
          <div>
            <Label className="text-xs font-semibold mb-1 block">Agence de rattachement (optionnel)</Label>
            <Select value={form.agency_id || "none"} onValueChange={(v) => setField("agency_id", v === "none" ? "" : v)}>
              <SelectTrigger className="rounded-none border-gray-300" data-testid="create-agency">
                <SelectValue placeholder="Aucune" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune (à compléter plus tard)</SelectItem>
                {agencies.map((a) => (
                  <SelectItem key={a.agency_id} value={a.agency_id}>{a.name} — {a.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#FAFAFA] border border-gray-100 p-3 text-[11px] text-gray-600 space-y-1">
            <p className="font-semibold text-[#662483]">À noter</p>
            <p>• Un mot de passe aléatoire est généré automatiquement. Le candidat utilisera <strong>"mot de passe oublié"</strong> sur la page de connexion pour en définir un.</p>
            <p>• L'adresse, la date de naissance, le n° de sécurité sociale, les pièces… sont à compléter depuis la fiche après création.</p>
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end gap-2 bg-[#FAFAFA]">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="create-cancel">
            Annuler
          </Button>
          <Button
            variant="outline"
            onClick={() => submit(false)}
            disabled={submitting}
            className="rounded-none"
            data-testid="create-only"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Créer
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={submitting}
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none"
            data-testid="create-and-open"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Créer et compléter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
